"use client";

import { useCallback, useState } from "react";

import {
  DEFAULT_FILTERS,
  DEFAULT_PLOTTING_COLOR_BY,
} from "@/app/(internal)/panel/(maps)/_constants/plotting-filters.constants";
import type { PlottingColorByEnum } from "@/app/(internal)/panel/(maps)/_enums/plotting-filters.enum";
import type { PlottingFiltersFormValues } from "@/app/(internal)/panel/(maps)/_schemas/plotting-filters.schema";
import { computeDefaultFilters } from "@/app/(internal)/panel/(maps)/_utils/plotting-filters.utils";
import type { PlottingGradeListboxOption } from "@/app/(internal)/panel/(maps)/_utils/plotting-filters.utils";
import type { ListboxFieldOption } from "@/components/flowbite/forms/fields/listbox/types";

export interface UsePlottingFiltersStateResult {
  filters: PlottingFiltersFormValues;
  setFiltersState: React.Dispatch<React.SetStateAction<PlottingFiltersFormValues>>;
  colorBy: PlottingColorByEnum;
  setColorByState: React.Dispatch<React.SetStateAction<PlottingColorByEnum>>;
  isApplied: boolean;
  setIsApplied: React.Dispatch<React.SetStateAction<boolean>>;
  hasSyncedWithBackend: boolean;
  setHasSyncedWithBackend: React.Dispatch<React.SetStateAction<boolean>>;
  /** Increments every time the form should be explicitly reset (Reset All, district change, preset applied). */
  resetKey: number;
  bumpResetKey: () => void;
  setFilters: (newFilters: Partial<PlottingFiltersFormValues>) => void;
  setColorBy: (newColorBy: PlottingColorByEnum) => void;
  applyFilters: () => void;
  resetFilters: () => void;
}

export function usePlottingFiltersState(
  selectedDistrictId: string | null,
  importPeriodOptions: ListboxFieldOption[],
  schoolTypeOptions: ListboxFieldOption[],
  gradeOptions: PlottingGradeListboxOption[],
): UsePlottingFiltersStateResult {
  const [colorBy, setColorByState] = useState<PlottingColorByEnum>(DEFAULT_PLOTTING_COLOR_BY);
  const [filters, setFiltersState] = useState<PlottingFiltersFormValues>(DEFAULT_FILTERS);
  const [isApplied, setIsApplied] = useState<boolean>(false);
  const [hasSyncedWithBackend, setHasSyncedWithBackend] = useState<boolean>(false);
  const [resetKey, setResetKey] = useState(0);

  const bumpResetKey = useCallback(() => setResetKey((prev) => prev + 1), []);

  const setColorBy = useCallback(
    (newColorBy: PlottingColorByEnum) => {
      if (!selectedDistrictId) return;
      setColorByState(newColorBy);
    },
    [selectedDistrictId],
  );

  const setFilters = useCallback(
    (newFilters: Partial<PlottingFiltersFormValues>) => {
      if (!selectedDistrictId) return;
      setFiltersState((prev) => ({ ...prev, ...newFilters }));
      setIsApplied(false);
    },
    [selectedDistrictId],
  );

  const applyFilters = useCallback(() => {
    if (!selectedDistrictId) return;
    setIsApplied(true);
  }, [selectedDistrictId]);

  const resetFilters = useCallback(() => {
    if (!selectedDistrictId) return;
    const { colorBy: defaultColorBy } = DEFAULT_FILTERS;
    setFiltersState({
      ...DEFAULT_FILTERS,
      ...computeDefaultFilters(DEFAULT_FILTERS, importPeriodOptions, schoolTypeOptions, gradeOptions),
    });
    setIsApplied(false);
    setColorByState(defaultColorBy);
    bumpResetKey();
  }, [selectedDistrictId, importPeriodOptions, schoolTypeOptions, gradeOptions, bumpResetKey]);

  return {
    filters,
    setFiltersState,
    colorBy,
    setColorByState,
    isApplied,
    setIsApplied,
    hasSyncedWithBackend,
    setHasSyncedWithBackend,
    resetKey,
    bumpResetKey,
    setFilters,
    setColorBy,
    applyFilters,
    resetFilters,
  };
}
